import React, { useState } from "react";
import { Sparkles, Loader2, Image as ImageIcon, Copy, Save, Send, Hash, KeyRound, FileText, ExternalLink, Wand2, Eye, UploadCloud, Images, Link as LinkIcon, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";

const inputCls = "h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200";
const areaCls = "min-h-[110px] w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm outline-none focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200";

function Pill({ children }) {
  return <span className="rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700">{children}</span>;
}

function ImageModeCard({ active, icon: Icon, title, subtitle, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl border p-3 text-left transition hover:shadow-soft ${active ? "border-brand-400 bg-brand-50 ring-2 ring-brand-100" : "border-slate-200 bg-white hover:border-brand-200"}`}
    >
      <div className="flex items-start gap-2">
        <Icon className={`mt-0.5 h-4 w-4 ${active ? "text-brand-700" : "text-slate-500"}`} />
        <div>
          <p className="text-sm font-semibold text-slate-800">{title}</p>
          <p className="mt-1 text-xs leading-5 text-slate-500">{subtitle}</p>
        </div>
      </div>
    </button>
  );
}

export default function AiPostGenerator() {
  const [form, setForm] = useState({
    topic: "",
    tone: "professional",
    language: "english",
    cta: "Contact us today",
    ctaUrl: "",
    mediaUrl: "",
    imageSource: "ai", // ai | stock | upload | url | none
    generateImage: true,
  });
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [post, setPost] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const copy = (t) => { navigator.clipboard.writeText(t || ""); toast.success("Copied"); };

  const buildPayload = () => ({
    ...form,
    generateImage: form.imageSource === "ai" || form.imageSource === "stock",
    mediaUrl: ["upload", "url"].includes(form.imageSource) ? form.mediaUrl : "",
  });

  const generate = async () => {
    if (!form.topic.trim()) return toast.info("Please enter a topic first");
    setLoading(true);
    setPost(null);
    try {
      const { data } = await api.post("/ai/gmb-package", buildPayload());
      setResult(data);
      toast.success(`Generated ${data.source === "ai" ? "with AI" : "with template"}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("mediaUrl", data.imageUrl);
      set("imageSource", "upload");
      setResult((r) => ({ ...(r || {}), imageUrl: data.imageUrl, imageProvider: "own_upload" }));
      toast.success("Own image uploaded");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const saveDraft = async () => {
    if (!result) return toast.info("Generate content first");
    try {
      const { data } = await api.post("/posts", {
        type: "update",
        title: result.title || form.topic,
        content: result.gmbSummary || result.caption,
        mediaUrl: form.mediaUrl || result.imageUrl || "",
        ctaType: result.ctaType || "LEARN_MORE",
        ctaUrl: result.ctaUrl || form.ctaUrl,
        status: "ai_generated",
      });
      setPost(data);
      toast.success("Saved in Posts as draft");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const generateImageOnly = async () => {
    if (form.imageSource === "url" || form.imageSource === "upload") {
      if (!form.mediaUrl) return toast.info("Please add/upload image first");
      setResult((r) => ({ ...(r || {}), imageUrl: form.mediaUrl, imageProvider: form.imageSource }));
      return toast.success("Image selected");
    }
    const prompt = result?.imagePrompt || form.topic;
    if (!prompt.trim()) return toast.info("Generate content or enter a topic first");
    setImageLoading(true);
    try {
      if (form.imageSource === "stock") {
        const { data } = await api.post("/ai/stock-image", { query: `${form.topic} ${result?.keywords?.join(" ") || ""}`.trim() });
        setResult((r) => ({ ...(r || {}), imageUrl: data.imageUrl, imageProvider: data.imageProvider, imageLicenseNote: data.licenseNote, photographer: data.photographer, photographerUrl: data.photographerUrl }));
        toast.success(data.imageProvider === "pexels" ? "Copyright-free stock image selected" : "Stock fallback image generated");
      } else {
        const { data } = await api.post("/ai/generate-image", { prompt, topic: form.topic });
        setResult((r) => ({ ...(r || {}), imageUrl: data.imageUrl, imageProvider: data.imageProvider, imagePrompt: data.prompt || prompt }));
        toast.success("AI image generated");
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setImageLoading(false);
    }
  };

  const generateAndSubmit = async () => {
    if (!form.topic.trim()) return toast.info("Please enter a topic first");
    setPublishing(true);
    try {
      const { data } = await api.post("/posts/quick-generate-publish", { ...buildPayload(), publishNow: true });
      setResult(data.package);
      setPost(data.post);
      if (data.post?.status === "published") toast.success("Generated and submitted to Google Business Profile");
      else toast.error(data.message || "Google publish failed. Saved in Posts as Failed.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPublishing(false);
    }
  };

  const previewImage = form.mediaUrl || result?.imageUrl || "";

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI Post Generator"
        subtitle="Enter one topic, generate caption, hashtags, keywords, choose image source, then save or submit to Google Business Profile."
        icon={Sparkles}
      />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[0.95fr_1.05fr]">
        <div className="card-surface space-y-4 p-6">
          <div className="rounded-2xl bg-gradient-to-br from-brand-700 to-brand-500 p-5 text-white shadow-soft">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">One-click GMB post</p>
            <h2 className="mt-2 font-heading text-2xl font-bold">Topic → Caption + Hashtags + Keywords + Image</h2>
            <p className="mt-2 text-sm text-white/80">Choose AI image, copyright-free stock, own upload, custom image URL, or no image.</p>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">Topic *</label>
            <input className={inputCls} value={form.topic} onChange={(e) => set("topic", e.target.value)} placeholder="e.g. Eid offer for website design in Hyderabad" data-testid="topic-input" />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Tone</label>
              <Select value={form.tone} onValueChange={(v) => set("tone", v)}>
                <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{["professional", "friendly", "premium", "sales focused", "local SEO focused"].map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Language</label>
              <Select value={form.language} onValueChange={(v) => set("language", v)}>
                <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{["english", "hinglish", "hindi"].map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">CTA Text</label>
              <input className={inputCls} value={form.cta} onChange={(e) => set("cta", e.target.value)} placeholder="Contact us today" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">CTA URL optional</label>
              <input className={inputCls} value={form.ctaUrl} onChange={(e) => set("ctaUrl", e.target.value)} placeholder="https://yourwebsite.com" />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
              <ImageIcon className="h-4 w-4 text-brand-700" /> Image Source
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ImageModeCard active={form.imageSource === "ai"} icon={Wand2} title="AI generated image" subtitle="Best for unique topic image. No extra key required." onClick={() => set("imageSource", "ai")} />
              <ImageModeCard active={form.imageSource === "stock"} icon={ShieldCheck} title="Copyright-free stock" subtitle="Uses Pexels if PEXELS_API_KEY is set; otherwise AI fallback." onClick={() => set("imageSource", "stock")} />
              <ImageModeCard active={form.imageSource === "upload"} icon={UploadCloud} title="Upload own image" subtitle="Client can upload JPG, PNG, or WEBP." onClick={() => set("imageSource", "upload")} />
              <ImageModeCard active={form.imageSource === "url"} icon={LinkIcon} title="Custom image URL" subtitle="Paste your own public image URL." onClick={() => set("imageSource", "url")} />
              <ImageModeCard active={form.imageSource === "none"} icon={Images} title="No image" subtitle="Publish text-only Google post." onClick={() => set("imageSource", "none")} />
            </div>

            {form.imageSource === "upload" && (
              <div className="mt-3 rounded-xl border border-dashed border-brand-200 bg-white p-4">
                <label className="mb-1 block text-xs font-semibold text-slate-600">Upload client image</label>
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => uploadImage(e.target.files?.[0])} className="block w-full text-sm text-slate-600" />
                {uploading && <p className="mt-2 text-xs text-brand-700">Uploading...</p>}
              </div>
            )}

            {(form.imageSource === "url" || form.imageSource === "upload") && (
              <div className="mt-3">
                <label className="mb-1 block text-xs font-semibold text-slate-600">Image URL</label>
                <input className={inputCls} value={form.mediaUrl} onChange={(e) => set("mediaUrl", e.target.value)} placeholder="https://yourdomain.com/image.jpg" />
                <p className="mt-1 text-xs text-slate-500">Google needs a public image URL. Uploaded image URL is filled automatically.</p>
              </div>
            )}

            {form.imageSource === "stock" && (
              <p className="mt-3 rounded-xl bg-emerald-50 p-3 text-xs text-emerald-700">
                For best copyright-free stock images, add <b>PEXELS_API_KEY</b> in Hostinger environment variables. If missing, the app uses AI-generated fallback.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Button onClick={generate} disabled={loading || publishing} className="h-11 rounded-xl bg-brand-700 text-white hover:bg-brand-800" data-testid="generate-all-btn">
              {loading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1 h-4 w-4" />} Generate All
            </Button>
            <Button onClick={generateAndSubmit} disabled={loading || publishing} className="h-11 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700" data-testid="generate-submit-gmb-btn">
              {publishing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />} Generate + Submit to GMB
            </Button>
          </div>

          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-700">
            Google publish requires: Google connected, active location selected, and Google Business Profile APIs enabled. Uploaded images must remain publicly accessible.
          </div>
        </div>

        <div className="card-surface space-y-4 p-6">
          {!result ? (
            <div className="flex h-full min-h-[360px] flex-col items-center justify-center text-center text-slate-400">
              <Sparkles className="mb-3 h-10 w-10" />
              <p className="font-medium">Generated caption, hashtags, keywords and image preview will appear here.</p>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-overline">Title</p>
                  <h3 className="font-heading text-xl font-bold text-slate-900">{result.title}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="rounded-xl" onClick={() => copy(result.gmbSummary || result.caption)}><Copy className="mr-1 h-3.5 w-3.5" /> Copy</Button>
                  <Button size="sm" className="rounded-xl bg-brand-700 text-white hover:bg-brand-800" onClick={saveDraft}><Save className="mr-1 h-3.5 w-3.5" /> Save Draft</Button>
                </div>
              </div>

              {post && (
                <div className={`rounded-2xl p-4 text-sm ${post.status === "published" ? "bg-emerald-50 text-emerald-700" : post.status === "failed" ? "bg-red-50 text-red-700" : "bg-brand-50 text-brand-700"}`}>
                  Post status: <b>{post.status}</b>{post.errorMessage ? ` — ${post.errorMessage}` : ""}
                </div>
              )}

              {previewImage && (
                <div>
                  <p className="text-overline flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" /> Image preview</p>
                  <div className="mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                    <img src={previewImage} alt="GMB post" className="aspect-square w-full object-cover" />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" className="rounded-xl" onClick={() => copy(previewImage)}><Copy className="mr-1 h-3.5 w-3.5" /> Copy Image URL</Button>
                    <Button size="sm" variant="outline" className="rounded-xl" onClick={() => window.open(previewImage, "_blank")}><Eye className="mr-1 h-3.5 w-3.5" /> Open Image</Button>
                    <Button size="sm" className="rounded-xl bg-brand-700 text-white hover:bg-brand-800" onClick={generateImageOnly} disabled={imageLoading || uploading}>{imageLoading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1 h-3.5 w-3.5" />} Regenerate Image</Button>
                  </div>
                  {result.imageProvider && <p className="mt-2 text-xs text-slate-500">Image source: {result.imageProvider}</p>}
                  {result.imageLicenseNote && <p className="mt-1 text-xs text-emerald-700">{result.imageLicenseNote}</p>}
                </div>
              )}

              {!previewImage && form.imageSource !== "none" && (
                <Button size="sm" className="rounded-xl bg-brand-700 text-white hover:bg-brand-800" onClick={generateImageOnly} disabled={imageLoading || uploading}>
                  {imageLoading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1 h-3.5 w-3.5" />} Generate / Select Image
                </Button>
              )}

              <div>
                <p className="text-overline flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Ready Google caption</p>
                <textarea className={areaCls} value={result.gmbSummary || result.caption || ""} readOnly />
              </div>

              <div>
                <p className="text-overline flex items-center gap-1"><Hash className="h-3.5 w-3.5" /> Hashtags</p>
                <div className="mt-2 flex flex-wrap gap-2">{(result.hashtags || []).map((h, i) => <Pill key={i}>{h}</Pill>)}</div>
              </div>

              <div>
                <p className="text-overline flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> Keywords</p>
                <div className="mt-2 flex flex-wrap gap-2">{(result.keywords || []).map((k, i) => <span key={i} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">{k}</span>)}</div>
              </div>

              <div>
                <p className="text-overline flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" /> Image prompt</p>
                <div className="mt-2 rounded-2xl bg-slate-50 p-4 text-sm leading-6 text-slate-600">{result.imagePrompt}</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="rounded-xl" onClick={() => copy(result.imagePrompt)}><Copy className="mr-1 h-3.5 w-3.5" /> Copy Image Prompt</Button>
                  <Button size="sm" className="rounded-xl bg-brand-700 text-white hover:bg-brand-800" onClick={generateImageOnly} disabled={imageLoading || uploading}>{imageLoading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1 h-3.5 w-3.5" />} Generate Image</Button>
                  {form.ctaUrl && <Button size="sm" variant="outline" className="rounded-xl" onClick={() => window.open(form.ctaUrl, "_blank")}><ExternalLink className="mr-1 h-3.5 w-3.5" /> CTA Link</Button>}
                </div>
              </div>

              {result.source === "template" && (
                <div className="rounded-2xl bg-amber-50 p-4 text-xs text-amber-700">
                  AI key not configured or model failed. This was generated using built-in template fallback.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
